/**
 * Chunk-aware check for a usable Supabase auth cookie in `document.cookie`.
 *
 * `document.cookie.includes("-auth-token")` is too loose: Supabase splits large
 * sessions into chunked cookies (`…-auth-token.0`, `.1`, …), so deleting or
 * emptying one chunk leaves the substring present while the session is broken —
 * the nav would read that as authenticated. This returns true only when a
 * non-empty unchunked cookie exists, or a contiguous `0..N` run of non-empty
 * chunks exists. Shape-only (no network); browser-side.
 */
export function hasUsableAuthCookie(): boolean {
  if (typeof document === "undefined") return false;

  const chunks = new Map<number, string>();
  let unchunked: string | null = null;

  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq === -1) continue;
    const name = trimmed.slice(0, eq);
    const value = trimmed.slice(eq + 1);
    if (!name.includes("-auth-token")) continue;

    const chunkMatch = name.match(/-auth-token\.(\d+)$/);
    if (chunkMatch) chunks.set(Number(chunkMatch[1]), value);
    else if (name.endsWith("-auth-token")) unchunked = value;
  }

  if (unchunked !== null) return unchunked.length > 0;
  if (chunks.size === 0) return false;

  // Require a contiguous 0..N run with every chunk non-empty.
  const indexes = [...chunks.keys()].sort((a, b) => a - b);
  for (let i = 0; i < indexes.length; i++) {
    if (indexes[i] !== i) return false;
    if ((chunks.get(i) ?? "").length === 0) return false;
  }
  return true;
}
